import { Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { ImportContext } from '../ImportDataButton';
import { Division, ReportingPeriod, Scope, Subdivision } from '@esg/esg-global-types';
import ReportingPeriodSelect from '../../shared/input/select/ReportingPeriodSelect';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { MetricExtended } from '../../../lib/metric_capture/metric';
import MetricSelect from '../../shared/input/select/MetricSelect';
import SiteSelect from '../../shared/input/select/SiteSelect';
import moment, { Moment } from 'moment';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { UserContext } from '../../../context/UserContext';
import {
  MetricRecordEntitiesMap,
  getMetricRecordEntitiesMap
} from '../../../graphql/services/metric_record.service';
import SkeletonGrid, { SkeletonItem } from '../../shared/loading/SkeletonGrid';
import { values } from '../../../util/general_utils';
import type { SiteExtended } from '../../../lib/metric_capture/site';
import { getCountryIdForSite } from '../../../graphql/utilities/metric_record.util';

// A representation of a "site" for the request body.
export interface ImportInputDataSite {
  id: string;
  glo_id: string;
  country_id: string | null;
  region_id: string;
  division_id: string;
  subdivision_id: string;
}

// A representation of a "metric" for the request body.
export interface ImportInputDataMetric {
  id: string;
  name: string;
  glo_id: string;
  scope?: Scope | null;
  metric_group_id: string;
  standard_id: string;
  require_emission_factor: boolean;
  is_calculated?: boolean;
  primary_metric_id?: string;
}

// A representation of a "reporting period" for the request body.
export interface ImportInputDataReportingPeriod {
  id: string;
  name: string;
  start: Moment;
  end: Moment;
  locked: boolean;
}

// A representation of a "standard" for the request body.
export interface ImportInputDataStandard {
  id: string;
  name: string;
}

// A representation of an "external company" for the request body.
export interface ImportInputDataExternalCompany {
  id: string;
}

// Data sent in request to unique Cloud Function.
interface IMetricRecordMeteredDataImportContext extends ImportContext {
  group_id: string;
  company_id: string;
  sites: Array<ImportInputDataSite> | null;
  metrics: Array<ImportInputDataMetric> | null;
  reporting_period: ImportInputDataReportingPeriod | null;
  external_company?: ImportInputDataExternalCompany | null;
  calculated_metrics?: Array<ImportInputDataMetric>;
}

// Data sent in request to unique Cloud Function.
export interface MetricRecordMeteredDataImportContextProps {
  handleContextChange: (context: IMetricRecordMeteredDataImportContext) => void;
  handleContextValid: (valid: boolean) => void;
}

/**
 * Reusable importing context for metric record Utility Management Metered data containing relevant inputs for the microservice.
 * @param {void} handleContextChange Update context in parent from input changes.
 * @param {void} handleContextValid Update context validity in parent based on input changes.
 * @returns {JSX.Element}
 */
const MetricRecordMeteredDataImportContext = ({
  handleContextChange,
  handleContextValid
}: MetricRecordMeteredDataImportContextProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  // Default context state for inputs.
  const initial_context: IMetricRecordMeteredDataImportContext = {
    group_id: group?.id ?? '',
    company_id: company?.id ?? '',
    reporting_period: null,
    external_company: null,
    metrics: [],
    sites: [],
    calculated_metrics: []
  };

  const initial_entities_map: MetricRecordEntitiesMap = {
    countries: {},
    regions: {},
    divisions: {},
    subdivisions: {},
    sites: {},
    reporting_period_groups: {},
    reporting_periods: {},
    metrics: {},
    emission_factors: {},
    external_companies: {}
  };

  const [entitiesMap, setEntitiesMap] = useState<MetricRecordEntitiesMap>(initial_entities_map);
  const [meteredDataImportContext, setMeteredDataImportContext] =
    useState<IMetricRecordMeteredDataImportContext>(initial_context);
  const [reportingPeriod, setReportingPeriod] = useState<ReportingPeriod | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<Array<MetricExtended>>([]);
  const [selectedSites, setSelectedSites] = useState<Array<SiteExtended>>([]);
  const [loadingEntities, setLoadingEntities] = useState<boolean>(true);

  const reporting_periods = values<ReportingPeriod>(entitiesMap.reporting_periods);
  const metrics = values<MetricExtended>(entitiesMap.metrics);
  const sites = values<SiteExtended>(entitiesMap.sites);

  // Only use sites for input which have a configured glo ID.
  const applicable_sites: Array<SiteExtended> = sites.filter(
    (site: SiteExtended) => typeof site.glo_id === 'string'
  );

  // Only use metrics for input which have a configured glo ID.
  const applicable_metrics: Array<MetricExtended> = metrics.filter(
    (metric: MetricExtended) => typeof metric.glo_id === 'string' && !metric.is_calculated
  );

  // Check for calculated metrics to include with request. EF resolution is done server-side.
  const calculated_request_metrics: Array<ImportInputDataMetric> = metrics
    .filter((metric: MetricExtended) => metric.is_calculated && metric.primary_metric_calculated)
    .map((metric: MetricExtended) => ({
      id: metric.id,
      name: metric.name,
      glo_id: metric.glo_id ?? '',
      scope: metric.scope,
      metric_group_id: metric.metric_group.id,
      standard_id: metric.standard.id,
      require_emission_factor:
        'require_emission_factor' in metric.standard && metric.standard.require_emission_factor,
      is_calculated: metric.is_calculated,
      primary_metric_id: metric.primary_metric_calculated!.id
    }));

  // Only use Reporting Periods far enough in the past (1 - 2 months based on current date).
  const current_day: number = moment().date();
  const reporting_period_cutoff_date: Moment = moment()
    .subtract(current_day >= 15 ? 1 : 2, 'month')
    .endOf('month');

  const applicable_reporting_periods: Array<ReportingPeriod> = reporting_periods.filter(
    (reporting_period: ReportingPeriod) =>
      moment(reporting_period.end) <= reporting_period_cutoff_date && !reporting_period.locked
  );

  // Validation check for unique import context.
  const context_valid = Boolean(
    meteredDataImportContext.group_id &&
    meteredDataImportContext.company_id &&
    meteredDataImportContext.reporting_period &&
    meteredDataImportContext.metrics &&
    meteredDataImportContext.sites &&
    meteredDataImportContext.metrics.length > 0 &&
    meteredDataImportContext.sites.length > 0
  );

  // Update validity of context in parent whenever valid check value changes.
  useEffect(() => {
    handleContextValid(context_valid);
  }, [context_valid, handleContextValid]);

  // Update validity of context in parent whenever valid check value changes.
  useEffect(() => {
    handleContextValid(context_valid);
  }, [context_valid, handleContextValid]);

  // Update reporting period state value and set request data value.
  // EF resolution is done server-side, so metric entries do not need updating when the period changes.
  const handleReportingPeriodChange = (
    reporting_period: Array<ReportingPeriod> | ReportingPeriod | null
  ): void => {
    if (!Array.isArray(reporting_period)) {
      const updated_context: IMetricRecordMeteredDataImportContext = {
        ...meteredDataImportContext,
        reporting_period: reporting_period
          ? {
              id: reporting_period.id,
              name: reporting_period.name,
              start: moment(reporting_period.start),
              end: moment(reporting_period.end),
              locked: reporting_period.locked
            }
          : null
      };
      setReportingPeriod(reporting_period);
      setMeteredDataImportContext(updated_context);
      handleContextChange(updated_context);
    }
  };

  // Update selected metrics state value and set request data value with corresponding emission factor ids for selected reporting period.
  const handleMetricsChange = (metrics: Array<MetricExtended> | MetricExtended | null): void => {
    if (Array.isArray(metrics)) {
      setSelectedMetrics(metrics);
      // Include relevant calculated
      const updated_context: IMetricRecordMeteredDataImportContext = {
        ...meteredDataImportContext,
        metrics: metrics.map((metric: MetricExtended) => {
          return {
            id: metric.id,
            name: metric.name,
            glo_id: metric.glo_id!,
            scope: metric.scope,
            metric_group_id: metric.metric_group.id,
            standard_id: metric.standard.id,
            require_emission_factor:
              'require_emission_factor' in metric.standard &&
              metric.standard.require_emission_factor
          };
        }),
        calculated_metrics: calculated_request_metrics
      };
      setMeteredDataImportContext(updated_context);
      handleContextChange(updated_context);
    }
  };

  // Update selected sites state value and set request data value with corresponding division id.
  const handleSitesChange = (sites: Array<SiteExtended> | SiteExtended | null): void => {
    if (Array.isArray(sites)) {
      const updated_context: IMetricRecordMeteredDataImportContext = {
        ...meteredDataImportContext,
        sites: sites.flatMap((site: SiteExtended) => {
          const site_subdivision: Subdivision | undefined =
            entitiesMap.subdivisions[site.subdivision.id];
          const site_division: Division | undefined = site_subdivision
            ? entitiesMap.divisions[site_subdivision.division.id]
            : undefined;
          return site_division
            ? [
                {
                  id: site.id,
                  glo_id: site.glo_id!,
                  country_id: getCountryIdForSite(site),
                  region_id: site.region.id,
                  subdivision_id: site.subdivision.id,
                  division_id: site_division.id
                }
              ]
            : [];
        })
      };
      setMeteredDataImportContext(updated_context);
      setSelectedSites(sites);
      handleContextChange(updated_context);
    }
  };

  // Fetch entities needed for import context inputs.
  const fetchMetricRecordEntitiesMap = async () => {
    setLoadingEntities(true);
    try {
      if (group && company && user) {
        const entities_map: MetricRecordEntitiesMap = await getMetricRecordEntitiesMap(
          group.id,
          company.id,
          user
        );
        if (entities_map) {
          setEntitiesMap({
            countries: entities_map.countries,
            regions: entities_map.regions,
            divisions: entities_map.divisions,
            subdivisions: entities_map.subdivisions,
            sites: entities_map.sites,
            reporting_period_groups: entities_map.reporting_period_groups,
            reporting_periods: entities_map.reporting_periods,
            metrics: entities_map.metrics,
            emission_factors: entities_map.emission_factors,
            external_companies: entities_map.external_companies
          });
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: MetricRecordMeteredDataImportContext failed while calling fetchMetricRecordEntitiesMap',
          {
            group: group,
            company: company,
            user: user
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch context entities. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  useEffect(() => {
    (async () => {
      setEntitiesMap(initial_entities_map);
      setMeteredDataImportContext(initial_context);
      setReportingPeriod(null);
      setSelectedMetrics([]);
      setSelectedSites([]);
      if (group && company && user) {
        await fetchMetricRecordEntitiesMap();
      }
    })();
  }, [user, company, group]);

  // Populate an array of skeleton items to be displayed while loading.
  const skeleton_grid_items: Array<SkeletonItem> = Array(3).fill({
    width: 250,
    height: 50,
    variant: 'rounded'
  });

  return (
    <>
      {loadingEntities ? (
        <SkeletonGrid skeleton_items={skeleton_grid_items} />
      ) : (
        <>
          <Grid size={'auto'} sx={{ minWidth: 250 }}>
            <ReportingPeriodSelect
              selected_reporting_periods={reportingPeriod}
              reporting_period_options={applicable_reporting_periods}
              handleChangeReportingPeriods={handleReportingPeriodChange}
            />
          </Grid>
          <Grid size={'auto'} sx={{ width: 400 }}>
            <MetricSelect
              input_label="Metrics"
              options={applicable_metrics}
              selected_options={selectedMetrics}
              handleChangeMetrics={handleMetricsChange}
              multi_select
              tag_limit={0}
              group_by="metric_group.name"
            />
          </Grid>
          <Grid size={'auto'} sx={{ width: 400 }}>
            <SiteSelect
              input_label="Sites"
              group_by="subdivision.division.name"
              options={applicable_sites}
              selected_options={selectedSites}
              handleChangeSites={
                handleSitesChange as Parameters<typeof SiteSelect>[0]['handleChangeSites']
              }
              multi_select
              tag_limit={0}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default MetricRecordMeteredDataImportContext;
